import brandImage1 from '@/assets/cases/babelcast/1.jpg';
import brandImage2A from '@/assets/cases/babelcast/2A.jpg';
import brandImage2B from '@/assets/cases/babelcast/2B.jpg';
import brandImage3 from '@/assets/cases/babelcast/3.jpg';
import brandImage4 from '@/assets/cases/babelcast/4.png';
import brandImage5 from '@/assets/cases/babelcast/5.jpg';
import brandImage6A from '@/assets/cases/babelcast/6a.jpg';
import brandImage6B from '@/assets/cases/babelcast/6b.jpg';
import heroVideo from '@/assets/cases/babelcast/0.mov';
import heroBlur from '@/assets/cases/babelcast/0.blur.webp';
import brandImage7 from '@/assets/cases/babelcast/7.mov';
import brandImage7B from '@/assets/cases/babelcast/7b.jpg';
import brandImage8 from '@/assets/cases/babelcast/8.mov';
import brandImage9A from '@/assets/cases/babelcast/9A.jpg';
import brandImage9B from '@/assets/cases/babelcast/9B.jpg';
import platformOverview from '@/assets/work-carousel/babelcast.png';
import BabelcastLogo from '@/components/Work/logos/BabelcastLogo';
import {CaseStudy} from './types';

const babelcast: CaseStudy = {
    // ============================================
    // FIXED METADATA
    // ============================================
    id: 'babelcast-v1',
    slug: 'babelcast-ai-content-platform',
    title: 'Babelcast',
    client: 'Babelcast',
    industry: 'Media & Content Creation',
    projectType: 'AI SaaS Platform Prototype',
    timeline: '1 weekend (Fall 2024)',
    technologies: ['React', 'TypeScript', 'AI/LLM Integration', 'Mobile-First Design'],

    shortDescription: 'Turned a one-page Word doc into an investor-ready AI platform prototype in 48 hours that closed a seed round.',
    excerpt: 'Built a complete AI SaaS prototype with brand identity, 40+ screens, and mobile experience in one weekend—demonstrating transcription, translation, and content automation when LLM features were just emerging.',
    heroImageUrl: '/images/case-studies/babelcast/hero.jpg',
    logo: BabelcastLogo,
    featured: true,
    workHighlight: {
        id: 'babelcast',
        mediaType: 'video',
        media: heroVideo,
        mediaSources: [{src: heroVideo, type: 'video/quicktime'}],
        placeholder: heroBlur
    },

    // ============================================
    // HERO SECTION DATA
    // ============================================
    heroTestimonial: 'They took a one-page Word doc and turned it into a prototype that looked like we\'d been building for months. Investors saw a complete product vision—brand, UX, AI features—and it got us funded.',
    heroStatement:
        'Built a complete AI platform prototype in 48 hours from a one-page Word doc. Brand identity, landing page, and 40+ screens demonstrating transcription, translation, and automation. Polished enough to close a seed round.',
    heroGalleryImage: platformOverview,
    heroMetrics: [
        {label: 'Screens designed', value: '40+'},
        {label: 'Hours', value: '48'}
    ],
    heroNarrative: 'The founder sent us a one-page Word doc Friday afternoon with investor meetings Monday. Fall 2024, when AI features were bleeding edge and most platforms were still figuring out LLM integration.\n\nWe delivered a complete prototype Sunday night: brand identity, landing page, and 40+ high-fidelity screens showing the full AI workflow. The prototype looked like it had been in development for months. He pitched with it. Seed round closed.',

    // ============================================
    // SCOPE OF WORK (4 Service Areas)
    // ============================================
    scopeOfWork: [
        {
            icon: 'palette',
            title: 'Brand Design',
            number: '01',
            description: 'Complete brand identity from scratch—logo, color system, typography, visual language. Positioned Babelcast as an AI-native platform for global content distribution.'
        },
        {
            icon: 'language',
            title: 'Marketing Site',
            number: '02',
            description: 'Landing page showing the AI workflow, value proposition, and platform capabilities. Investor-facing asset that communicated the vision before they saw the product.'
        },
        {
            icon: 'design_services',
            title: 'UI/UX Design',
            number: '03',
            description: 'Mobile-first interface across 40+ screens—onboarding, upload, AI processing, transcription editing, shorts generation, analytics, scheduling. Every interaction designed.'
        },
        {
            icon: 'code',
            title: 'Prototype Development',
            number: '04',
            description: 'High-fidelity interactive prototype demonstrating the complete AI workflow. Looked production-ready, flowed like working software.'
        }
    ],

    // ============================================
    // CASE DETAILS (Collapsible Card)
    // ============================================
    caseDetails: {
        task:
            'Turn a one-page concept into an investor-ready prototype in one weekend—complete with brand identity, product design, and AI features that demonstrated the full platform vision.',
        solution:
            'Built complete brand system and designed 40+ screens across 7 core workflows: onboarding, content management, AI transcription, translation, shorts generation, analytics, scheduling. Prototype showed investors exactly what the platform would do.',
        result:
            'Investors saw a complete platform vision in a polished prototype that looked production-bound. The combination of brand quality, thoughtful UX, and innovative AI features made the case. Seed round closed within the week.',
        testimonial: {
            quote: 'They took a one-page Word doc and turned it into a prototype that looked like we\'d been building for months. The brand was polished, the UX was intuitive, and investors could see the complete AI workflow. That\'s what got us funded.',
            author: 'Jacob Zimmerman',
            role: 'Founder, Babelcast',
            avatarUrl: 'https://media.licdn.com/dms/image/v2/C4E03AQHzzB_Pp_XCbw/profile-displayphoto-shrink_800_800/profile-displayphoto-shrink_800_800/0/1589315880340?e=1763596800&v=beta&t=4HUE9SbD6bKvVZRY4eZ1jsbJDX_31Fc0VuZoKLTM-NA'
        },
        stack: ['Figma', 'React', 'TypeScript', 'Interactive Prototype', 'Mobile-First']
    },

    // ============================================
    // DYNAMIC CONTENT (Visual Storytelling)
    // ============================================
    content: [
        // BRAND IDENTITY SECTION
        {
            type: 'heading2',
            content: 'Brand Identity',
            id: 'brand',
            description: 'Strategic differentiation in a crowded AI market'
        },
        {
            type: 'paragraph',
            content: 'The AI content space was getting crowded. ElevenLabs owned blue. Voice.ai had green. Traditional SaaS branding everywhere. We chose a strong, memorable yellow—optimistic, energetic, impossible to ignore. The brand needed to signal "AI-native" and "global-first" while standing apart from the competition.'
        },
        {
            type: 'paragraph',
            content: 'Complete visual system: logo, color palette, typography, component library. Modern, approachable, trustworthy. The landing page became the first investor touchpoint—polished brand before they saw the prototype. Context matters when you\'re raising money.'
        },

        // Brand Gallery (single-double-single cadence)
        {
            type: 'gallery',
            content: {
                rows: [
                    {
                        layout: 'single',
                        images: [
                            {
                                src: brandImage1,
                                alt: 'Babelcast logo and brand identity',
                                caption: 'Logo design with global connectivity concept',
                            }
                        ],
                    },
                    {
                        layout: 'double',
                        images: [
                            {
                                src: brandImage2A,
                                alt: 'Complete brand system',
                                caption: 'Color palette, typography, and visual identity system',
                            },
                            {
                                src: brandImage2B,
                                alt: 'Landing page hero section',
                                caption: 'Landing page demonstrating AI workflow and value prop',
                            }
                        ],
                    },
                    {
                        layout: 'single',
                        images: [
                            {
                                src: brandImage3,
                                alt: 'Full landing page design',
                                caption: 'Complete landing page with features and positioning',
                            }
                        ],
                    },
                ],
            },
        },

        // PLATFORM CAPABILITIES SECTION
        {
            type: 'heading2',
            content: 'Platform Design',
            id: 'platform',
            description: '40+ screens demonstrating complete AI workflow'
        },
        {
            type: 'paragraph',
            content: 'Upload a podcast. AI transcribes it, translates into 20+ languages, generates social clips, schedules distribution. The entire workflow designed and prototyped. Fall 2024—when most platforms were still figuring out how to integrate LLMs, Babelcast was showing investors a complete AI automation stack.'
        },
        {
            type: 'gallery',
            content: {
                rows: [
                    {
                        layout: 'single',
                        images: [
                            {
                                src: brandImage4,
                                alt: 'Babelcast platform overview',
                                caption: 'Complete platform showing AI workflow from upload to distribution',
                            }
                        ],
                    },
                ],
            },
        },
        {
            type: 'comparisonTable',
            content: {
                headers: ['Creator Problem', 'Babelcast Solution'],
                rows: [
                    ['Manual transcription: $100-300 per episode, 2-3 days', 'AI transcription: 5-10 minutes, speaker detection, 20+ languages'],
                    ['Translation: $100-300 per language with human teams', 'AI translation: unlimited languages, context-aware, instant'],
                    ['Social clips: 2-3 hours of editing per episode', 'AI shorts: 30 seconds, intelligent clip selection'],
                    ['Distribution: manual posting to each platform', 'Scheduling: one-click distribution, multi-platform'],
                ],
            },
        },

        {
            type: 'gallery',
            content: {
                heading: 'Onboarding & Content Management',
                caption: 'Typeform-inspired onboarding flow, mobile-optimized dashboard, searchable content library.',
                rows: [
                    {
                        layout: 'single',
                        images: [
                            {
                                src: brandImage5,
                                alt: 'Typeform-inspired onboarding flow',
                                caption: 'Onboarding flow explaining the AI workflow',
                            },
                        ],
                    },
                    {
                        layout: 'double',
                        images: [
                            {
                                src: brandImage6A,
                                alt: 'Content dashboard',
                                caption: 'Mobile-optimized content management',
                            },
                            {
                                src: brandImage6B,
                                alt: 'Content library',
                                caption: 'Searchable library with filters and status',
                            },
                        ],
                    },
                ],
            },
        },

        {
            type: 'gallery',
            content: {
                heading: 'Upload & AI Processing',
                caption: 'Drag-and-drop upload with real-time job monitoring showing transcription, translation, and shorts generation progress.',
                rows: [
                    {
                        layout: 'double',
                        images: [
                            {
                                src: brandImage7,
                                alt: 'Content upload interface',
                                caption: 'Upload interface with AI processing queue',
                                kind: 'video',
                                autoPlay: true,
                                loop: true,
                                muted: true,
                                playsInline: true,
                            },
                            {
                                src: brandImage7B,
                                alt: 'AI job monitoring',
                                caption: 'Real-time monitoring of AI jobs',
                            },
                        ],
                    },
                ],
            },
        },

        {
            type: 'gallery',
            content: {
                heading: 'Transcriptions & Shorts',
                caption: 'AI-generated transcriptions in 20+ languages with inline editing, plus automatic short-form content for social platforms.',
                rows: [
                    {
                        layout: 'single',
                        images: [
                            {
                                src: brandImage8,
                                alt: 'Transcriptions and shorts interface',
                                caption: 'AI-generated transcriptions and short-form content',
                                kind: 'video',
                                autoPlay: true,
                                loop: true,
                                muted: true,
                                playsInline: true,
                            },
                        ],
                    },
                ],
            },
        },

        {
            type: 'gallery',
            content: {
                heading: 'Analytics & Scheduling',
                caption: 'Global analytics by region and language, multi-platform scheduling with optimized formatting per channel.',
                rows: [
                    {
                        layout: 'double',
                        images: [
                            {
                                src: brandImage9A,
                                alt: 'Global analytics dashboard',
                                caption: 'Analytics showing global reach by region',
                            },
                            {
                                src: brandImage9B,
                                alt: 'Channel content scheduler',
                                caption: 'Multi-platform scheduling interface',
                            },
                        ],
                    },
                ],
            },
        },

        {
            type: 'divider'
        },

        // TECHNICAL FEATS SECTION
        {
            type: 'heading2',
            content: 'The Innovation',
            id: 'innovation',
            description: 'Early AI features and mobile-first design'
        },
        {
            type: 'paragraph',
            content: 'Fall 2024. LLM features were just emerging. Most platforms were adding AI as an afterthought. Babelcast was designed AI-native from the start: chat interface, transcription, translation, clip generation—all working together as a unified workflow. Mobile-first meant creators could manage everything from their phones.'
        },

        {
            type: 'keyBulletList',
            content: {
                heading: 'AI-Native Workflow',
                description: 'Designed when AI integration was still bleeding edge.',
                points: [
                    {
                        icon: 'auto_awesome',
                        title: 'AI Transcription',
                        text: 'Speaker detection, timestamps, 20+ languages. Demonstrated the flow from upload to completed transcription in minutes.'
                    },
                    {
                        icon: 'translate',
                        title: 'Smart Translation',
                        text: 'Context-aware translation preserving tone and meaning. Showed unlimited language support—no per-language cost.'
                    },
                    {
                        icon: 'content_cut',
                        title: 'Clip Generation',
                        text: 'AI identifying key moments and cutting social clips automatically. Demonstrated 30-second workflow replacing hours of editing.'
                    },
                    {
                        icon: 'speed',
                        title: 'Background Processing',
                        text: 'Job queue with progress monitoring. Designed the UX of waiting—status updates, notifications, completion.'
                    }
                ]
            }
        },

        {
            type: 'keyBulletList',
            content: {
                heading: 'Mobile-First Design',
                description: 'Built for how creators actually work—on their phones.',
                points: [
                    {
                        icon: 'phone_iphone',
                        title: 'Touch-First Interface',
                        text: 'Every screen optimized for mobile. Drag-and-drop, swipe gestures, thumb-friendly buttons. One-handed operation.'
                    },
                    {
                        icon: 'devices',
                        title: 'Responsive Across Devices',
                        text: 'Phone, tablet, desktop—same features, adaptive layouts. Showed how the platform would work anywhere.'
                    },
                    {
                        icon: 'notifications',
                        title: 'Smart Notifications',
                        text: 'Designed notification system for job completion, analytics milestones, scheduling. Kept creators informed without overwhelming.'
                    }
                ]
            }
        },

    ]
};

export default babelcast;
